import { z } from "zod";

import type { ConditionState } from "./event-conditions";
import { TAG_RESOURCE_TYPES } from "./tags";
import {
  TASK_PRIORITIES,
  TASK_STATUS_DISPLAY_ORDER,
  TASK_STATUS_LABELS,
  TASK_STATUSES,
} from "./tasks-api";
import { WEBHOOK_EVENT_TYPES, type WebhookEventType } from "./webhook-events";
import type {
  WorkflowTriggerConditionField,
  WorkflowTriggerEventMetadata,
} from "./workflow-trigger-definition";

/**
 * The platform event bus's registry: every event type that may be emitted,
 * who may see it, and what a subscriber needs to match it. The bus is the
 * `event_outbox` → `events` path; see docs/design-docs/platform-event-bus.md.
 *
 * `public` events are the customer contract (`GET /v1/events`, webhooks).
 * `internal` events are stored and fanned out to internal subscribers only.
 */

export type EventAudience = "public" | "internal";

/** The thing an event is about, named the way `PUBLIC_ID_PREFIXES` names resources. */
export type EventSubject = { type: string; publicId: string };

/** Who caused the event. `thread` is the agent run that made the change, when one did. */
export type EventActor = {
  /** `api_client` is an API key or OAuth client; `system` is the platform itself. */
  type: "user" | "agent" | "api_client" | "system";
  id?: string;
  thread?: string;
  /** The workflow run that made the change, when one did: what stops a workflow from starting itself. */
  workflowRun?: string;
};

export type PlatformEventDefinition = {
  audience: EventAudience;
  data: z.ZodType<Record<string, unknown>>;
  /** Kinds of subject a listener may name for this event. */
  subjectTypes: readonly string[];
  /** Subjects beyond the event's own that a listener may match on (an email's mailbox). */
  extraSubjects?: (data: Record<string, unknown>) => EventSubject[];
  /** Fields a condition may name, and the operators each supports. */
  conditionFields: Readonly<Record<string, WorkflowTriggerConditionField>>;
  /** Present when this event should be discovered by the workflow editor. */
  workflowTrigger?: WorkflowTriggerEventMetadata;
  /** True for something arriving (an email, a comment) rather than a subject changing state. */
  arrival: boolean;
  /** The subject's condition fields after the event, read from its data. */
  stateAfter?: (data: Record<string, unknown>) => ConditionState;
  /** The fields this event changed, read from its data. */
  changedFields?: (data: Record<string, unknown>) => string[];
};

export type PlatformEventRegistry = Readonly<Record<string, PlatformEventDefinition>>;

const snapshot = z.record(z.string(), z.unknown());
const text = { kind: "text" } as const;
const noConditionFields = {} satisfies Readonly<Record<string, WorkflowTriggerConditionField>>;

function fieldLabel(field: string): string {
  const words = field.replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function unwrapField(schema: z.ZodType): { schema: z.ZodType; nullable: boolean } {
  let current = schema;
  let nullable = false;
  while (current instanceof z.ZodOptional || current instanceof z.ZodNullable) {
    nullable = true;
    current = (current as unknown as { _def: { innerType: z.ZodType } })._def.innerType;
  }
  return { schema: current, nullable };
}

/** Scalar payload fields become workflow filters without a second field catalog. */
export function deriveWorkflowTriggerConditionFields(
  schema: z.ZodType<Record<string, unknown>>,
): Readonly<Record<string, WorkflowTriggerConditionField>> {
  if (!(schema instanceof z.ZodObject)) return noConditionFields;
  const fields: Record<string, WorkflowTriggerConditionField> = {};
  for (const [field, rawSchema] of Object.entries(schema.shape)) {
    const { schema: valueSchema, nullable } = unwrapField(rawSchema as z.ZodType);
    const presence = nullable ? (["is_null", "is_not_null"] as const) : [];
    if (valueSchema instanceof z.ZodEnum) {
      fields[field] = {
        label: fieldLabel(field),
        operators: ["eq", "neq", ...presence],
        input: {
          kind: "select",
          options: valueSchema.options
            .filter((value): value is string => typeof value === "string")
            .map((value) => ({ value, label: fieldLabel(value) })),
        },
      };
    } else if (valueSchema instanceof z.ZodBoolean) {
      fields[field] = {
        label: fieldLabel(field),
        operators: ["eq", "neq", ...presence],
        input: {
          kind: "select",
          options: [
            { value: "true", label: "True" },
            { value: "false", label: "False" },
          ],
        },
      };
    } else if (valueSchema instanceof z.ZodNumber) {
      fields[field] = {
        label: fieldLabel(field),
        operators: ["eq", "neq", "gt", "gte", "lt", "lte", ...presence],
        input: text,
      };
    } else if (valueSchema instanceof z.ZodString) {
      const textOperators =
        field.endsWith("_id") || field.endsWith("_ids")
          ? (["eq", "neq"] as const)
          : (["eq", "neq", "contains", "not_contains"] as const);
      fields[field] = {
        label: fieldLabel(field),
        operators: [...textOperators, ...presence],
        input: text,
      };
    }
  }
  return fields;
}

function publicEvent(subjectType: string): PlatformEventDefinition {
  return {
    audience: "public",
    // A public event's data is the resource's GET response (docs/design-docs/events.md).
    data: snapshot,
    subjectTypes: [subjectType],
    conditionFields: noConditionFields,
    arrival: false,
  };
}

const PUBLIC_EVENTS: Record<WebhookEventType, PlatformEventDefinition> = {
  "classification_run.created": publicEvent("classification_run"),
  "classification_run.completed": publicEvent("classification_run"),
  "classification_run.failed": publicEvent("classification_run"),
  "eval_experiment.completed": publicEvent("eval_experiment"),
  "eval_experiment.failed": publicEvent("eval_experiment"),
};

/**
 * Internal event types join here, one entry each. The public entries are
 * exactly `WEBHOOK_EVENT_TYPES`: the list a customer can subscribe to grows
 * only by adding to that list.
 */
/** A tag put on or taken off a resource. The subject is the tagged resource. */
const tagChange = {
  audience: "internal",
  data: z.object({ tag_id: z.string() }).strict(),
  subjectTypes: TAG_RESOURCE_TYPES,
  conditionFields: noConditionFields,
  workflowTrigger: {
    triggerType: "tag_applied",
    label: "Tag applied",
    icon: "tag",
    delivery: { kind: "custom" },
  } as const,
  arrival: true,
} satisfies PlatformEventDefinition;

/** Something a person is notified about: one of `NOTIFICATION_TYPE_REGISTRY`. */
function notificationEvent(
  subjectType: string,
  data: z.ZodType<Record<string, unknown>>,
): PlatformEventDefinition {
  return {
    audience: "internal",
    data,
    subjectTypes: [subjectType],
    conditionFields: noConditionFields,
    arrival: false,
  };
}

const bridgePresenceChange = z
  .object({ bridge_id: z.string(), bridge_name: z.string(), offline_since: z.string() })
  .strict();
/** `endpoint_url` is host and path only: a receiver's query string can carry its credentials. */
const webhookEndpointFailing = z
  .object({
    webhook_endpoint_id: z.string(),
    endpoint_url: z.string(),
    failing_since: z.string(),
    disable_at: z.string(),
    last_http_status: z.number().int().nullable(),
  })
  .strict();
const webhookEndpointDisabled = z
  .object({
    webhook_endpoint_id: z.string(),
    endpoint_url: z.string(),
    reason: z.enum(["auto_disabled_no_successful_delivery", "outbound_oauth_delivery_failed"]),
    failing_since: z.string(),
  })
  .strict();
const workflowRunOutcome = notificationEvent(
  "workflow_run",
  z.object({ workflow_run_id: z.string(), status: z.enum(["completed", "failed"]) }).strict(),
);
const extractionOutcome = notificationEvent(
  "extraction_run",
  z
    .object({
      extraction_id: z.string(),
      submission_id: z.string().nullable(),
      status: z.enum(["completed", "failed"]),
    })
    .strict(),
);

const customRecordSnapshot = z
  .object({
    custom_model_id: z.string(),
    custom_model_name: z.string(),
    custom_model_version: z.number().int(),
    data: z.record(z.string(), z.unknown()),
  })
  .strict();

const taskCreatedData = z
  .object({
    title: z.string(),
    description: z.string().nullable(),
    status: z.enum(TASK_STATUSES),
    priority: z.enum(TASK_PRIORITIES),
    assignee_type: z.enum(["user", "agent"]).nullable(),
    assignee_user_id: z.string().nullable(),
    assignee_agent_id: z.string().nullable(),
    due_date: z.string().nullable(),
  })
  .strict();

const INTERNAL_EVENTS = {
  "workspace.github.push_received": {
    audience: "internal",
    data: z.object({ connection_id: z.string(), delivery_id: z.string().uuid() }).strict(),
    subjectTypes: ["workspace_github_connection"],
    conditionFields: noConditionFields,
    arrival: false,
  },

  "agent.delegation_ended": {
    audience: "internal",
    data: z.object({ delegation_id: z.string() }).strict(),
    subjectTypes: ["agent_thread"],
    conditionFields: noConditionFields,
    arrival: true,
  },
  "tag.applied": tagChange,
  "tag.removed": tagChange,
  "workflow_run.completed": workflowRunOutcome,
  "workflow_run.failed": workflowRunOutcome,
  "extraction.completed": extractionOutcome,
  "extraction.failed": extractionOutcome,
  /** An export file is ready. The labels are what the notification's title is built from. */
  "export.succeeded": notificationEvent(
    "export",
    z
      .object({ export_id: z.string(), kind_label: z.string(), resource_label: z.string() })
      .strict(),
  ),
  /** A custom-record row was created, changed or deleted through its audited write chokepoint. */
  "custom_record.created": {
    audience: "internal",
    data: customRecordSnapshot,
    subjectTypes: ["custom_record"],
    conditionFields: deriveWorkflowTriggerConditionFields(customRecordSnapshot),
    workflowTrigger: {
      triggerType: "record_created",
      label: "Record created",
      icon: "event",
      focused: true,
      delivery: {
        kind: "object_change",
        resourceIdInput: "recordId",
        statusLine: "Queued from a record creation.",
      },
    } as const,
    arrival: true,
  },
  "custom_record.updated": {
    audience: "internal",
    data: customRecordSnapshot.extend({
      changed: z.array(z.string()),
      changes: z.record(z.string(), z.object({ from: z.unknown(), to: z.unknown() }).strict()),
    }),
    subjectTypes: ["custom_record"],
    conditionFields: noConditionFields,
    arrival: false,
  },
  "custom_record.deleted": {
    audience: "internal",
    data: customRecordSnapshot,
    subjectTypes: ["custom_record"],
    conditionFields: noConditionFields,
    arrival: true,
  },
  /** A task row was created. The subject is the new task. */
  "task.created": {
    audience: "internal",
    data: taskCreatedData,
    subjectTypes: ["task"],
    conditionFields: deriveWorkflowTriggerConditionFields(taskCreatedData),
    workflowTrigger: {
      triggerType: "task_created",
      label: "Task created",
      icon: "task",
      focused: true,
      delivery: {
        kind: "object_change",
        resourceIdInput: "taskId",
        statusLine: "Queued from a task creation.",
      },
    } as const,
    arrival: true,
  },
  /** A task row was soft-deleted. Its payload remains useful after the tombstone hides the row. */
  "task.deleted": {
    audience: "internal",
    data: z
      .object({
        title: z.string(),
        status: z.enum(TASK_STATUSES),
        previous_status: z.enum(TASK_STATUSES),
        deleted_at: z.string().datetime(),
        deleted_with_parent_id: z.string().nullable(),
        deleted_subtask_ids: z.array(z.string()),
      })
      .strict(),
    subjectTypes: ["task"],
    conditionFields: noConditionFields,
    arrival: false,
  },
  /**
   * An agent run stopped for a person: an open `agent_thread_input_requests` row.
   * Recipients are read from the request, not carried here.
   */
  "agent_thread.input_requested": notificationEvent(
    "agent_thread",
    z
      .object({
        request_id: z.string(),
        thread_id: z.string(),
        agent_id: z.string(),
        kind: z.enum(["question", "approval"]),
        summary: z.string(),
        tool_name: z.string().nullable(),
      })
      .strict(),
  ),
  /**
   * A person became a task's assignee, on create or by reassignment. Only that person is
   * notified, and not when they assigned it to themselves; the actor says who assigned it.
   */
  "task.assigned": notificationEvent(
    "task",
    z
      .object({
        task_id: z.string(),
        title: z.string(),
        assignee_user_id: z.string(),
        due_date: z.string().nullable(),
      })
      .strict(),
  ),
  /**
   * Every replica of a bridge that has connected before has been gone for five minutes, or
   * one is back after such an outage. Emitted by the worker's presence sweep; owners and
   * admins of the workspace are notified.
   */
  "bridge.offline": notificationEvent("bridge", bridgePresenceChange),
  "bridge.back_online": notificationEvent("bridge", bridgePresenceChange),
  /**
   * No delivery to a webhook endpoint has succeeded for 2.5 hours, or the delivery worker turned it
   * off. Emitted by the delivery worker; owners and admins of the workspace are notified.
   */
  "webhook_endpoint.failing": notificationEvent("webhook_endpoint", webhookEndpointFailing),
  "webhook_endpoint.disabled": notificationEvent("webhook_endpoint", webhookEndpointDisabled),
  /**
   * A task row changed. Named and shaped like the task feed's `task.updated`
   * (`TaskEventType.Updated`): any field, status included.
   */
  "task.updated": {
    audience: "internal",
    data: z
      .object({
        title: z.string(),
        changed: z.array(z.string()).min(1),
        changes: z.record(z.string(), z.object({ from: z.unknown(), to: z.unknown() })),
        status: z.string(),
        assignee_type: z.string().nullable(),
        assignee_user_id: z.string().nullable(),
        assignee_agent_id: z.string().nullable(),
      })
      .strict(),
    subjectTypes: ["task"],
    conditionFields: {
      status: {
        label: "Status",
        operators: ["eq", "neq"],
        input: {
          kind: "select",
          options: TASK_STATUS_DISPLAY_ORDER.map((value) => ({
            value,
            label: TASK_STATUS_LABELS[value],
          })),
        },
      },
    },
    workflowTrigger: {
      triggerType: "task_updated",
      label: "Task updated",
      icon: "task",
      delivery: {
        kind: "object_change",
        resourceIdInput: "taskId",
        statusLine: "Queued from a task update.",
        previousValues: { status: { input: "previousStatus", valueType: "string" } },
      },
    } as const,
    arrival: false,
    stateAfter: (data) => ({ status: String(data.status) }),
    changedFields: (data) => data.changed as string[],
  },
  /** A person or an agent commented on a task. The comment is read from the task, not carried here. */
  "task.comment_added": {
    audience: "internal",
    data: z.object({ comment_id: z.string(), title: z.string() }).strict(),
    subjectTypes: ["task"],
    conditionFields: noConditionFields,
    arrival: true,
  },
  /**
   * A task's agent run ended: the typed outcome of one run, so a coordinator
   * hears of a child that failed, was canceled or ended `unknown`, none of
   * which changes the task's own status.
   */
  "task.run_ended": {
    audience: "internal",
    data: z
      .object({
        title: z.string(),
        thread_id: z.string(),
        outcome: z.enum(["succeeded", "failed", "canceled", "unknown"]),
        code: z.string().nullable(),
        reason: z.string().nullable(),
      })
      .strict(),
    subjectTypes: ["task"],
    conditionFields: {
      outcome: {
        label: "Outcome",
        operators: ["eq", "neq"],
        input: {
          kind: "select",
          options: ["succeeded", "failed", "canceled", "unknown"].map((value) => ({
            value,
            label: fieldLabel(value),
          })),
        },
      },
    },
    arrival: false,
    stateAfter: (data) => ({ outcome: String(data.outcome) }),
    changedFields: () => ["outcome"],
  },
  "issue.opened": {
    audience: "internal",
    data: z
      .object({
        grader_id: z.string(),
        title: z.string(),
        opened_by: z.enum(["person", "improver", "grader"]),
      })
      .strict(),
    subjectTypes: ["issue"],
    conditionFields: noConditionFields,
    arrival: false,
  },
  /**
   * A grader settled a verdict. `passed` is judged against the threshold at
   * the time; issue triage hears the failing ones. Status `failed` (the grader's
   * run ended without a verdict) is not a verdict and raises nothing.
   */
  "grade.graded": {
    audience: "internal",
    data: z
      .object({
        grader_id: z.string(),
        passed: z.boolean(),
        classification: z.string(),
      })
      .strict(),
    subjectTypes: ["grade"],
    conditionFields: noConditionFields,
    arrival: false,
  },
  /**
   * A run of an agent thread ended: a queued run reached a terminal status, or a
   * chat turn settled. Every source announces it, so a consumer that judges or
   * counts runs reads one event type; `task.run_ended` is the task's view of the
   * same moment. The data names the run, never its transcript: a consumer reads
   * that with the thread tools.
   */
  "agent_thread.run_ended": {
    audience: "internal",
    data: z
      .object({
        agent_id: z.string(),
        agent_version_id: z.string().nullable(),
        task_id: z.string().nullable(),
        source: z.string(),
        outcome: z.enum(["succeeded", "failed", "canceled", "unknown"]),
        code: z.string().nullable(),
        reason: z.string().nullable(),
      })
      .strict(),
    subjectTypes: ["agent_thread"],
    conditionFields: {
      outcome: {
        label: "Outcome",
        operators: ["eq", "neq"],
        input: {
          kind: "select",
          options: ["succeeded", "failed", "canceled", "unknown"].map((value) => ({
            value,
            label: fieldLabel(value),
          })),
        },
      },
    },
    arrival: false,
    stateAfter: (data) => ({ outcome: String(data.outcome) }),
    changedFields: () => ["outcome"],
  },
  /**
   * An inbound email was accepted for processing. The event is a pointer: the
   * message is read with `get_email`, never carried here. A listener may name
   * the email thread or the mailbox.
   */
  "email.received": {
    audience: "internal",
    data: z
      .object({
        message_id: z.string(),
        email_thread_id: z.string().nullable(),
        mailbox_id: z.string().nullable(),
      })
      .strict(),
    subjectTypes: ["email_thread", "integration_mailbox"],
    extraSubjects: (data) =>
      typeof data.mailbox_id === "string"
        ? [{ type: "integration_mailbox", publicId: data.mailbox_id }]
        : [],
    // The email trigger's fields, matched by the email trigger's own matcher over the message.
    conditionFields: {
      from: { label: "From", operators: ["eq", "contains"], input: text },
      to: { label: "To", operators: ["eq", "contains"], input: text },
      subject: { label: "Subject", operators: ["eq", "contains"], input: text },
      body: { label: "Body", operators: ["eq", "contains"], input: text },
      attachment_filename: {
        label: "Attachment filename",
        operators: ["eq", "contains", "not_contains"],
        input: text,
      },
      attachment_content_type: {
        label: "Attachment content type",
        operators: ["eq", "contains", "not_contains"],
        input: text,
      },
      tag: { label: "Tag", operators: ["eq", "neq"], input: text },
    },
    workflowTrigger: {
      triggerType: "email_received",
      label: "Email received",
      icon: "mail",
      delivery: { kind: "custom" },
    } as const,
    arrival: true,
  },
} satisfies Record<string, PlatformEventDefinition>;

const platformEvents = {
  ...PUBLIC_EVENTS,
  ...INTERNAL_EVENTS,
} satisfies PlatformEventRegistry;
export const PLATFORM_EVENT_DEFINITIONS = platformEvents;
export const PLATFORM_EVENTS: PlatformEventRegistry = platformEvents;

export type PlatformEventType = WebhookEventType | keyof typeof INTERNAL_EVENTS;

export const PUBLIC_PLATFORM_EVENT_TYPES: readonly string[] = WEBHOOK_EVENT_TYPES;

/** Every subject a listener may match an event on: its own, then any the type adds. */
export function eventSubjects(
  definition: PlatformEventDefinition,
  event: { subject: EventSubject | null; data: Record<string, unknown> },
): EventSubject[] {
  return [
    ...(event.subject ? [event.subject] : []),
    ...(definition.extraSubjects?.(event.data) ?? []),
  ];
}
